/**
 * This dialog prompts the user for which type of feedback they would like to provide.
 * Ultimately this directs to either the customer feedback URL or our own feedback form.
 *
 * It the customer has not defined a URL for feedback, this dialog is skipped and user
 * is taken directly to the feedback form.
 */

import React, { useState, useEffect } from 'react'
import styled from 'styled-components'

import { Button } from '../../../../src/ui/buttons/Button.jsx'
import Radio from '../../../../src/ui/buttons/Radio.jsx'
import { withModalWindow } from '../../../../src/ui/modals/ModalWindowMol.jsx'

import { smile, frown } from './faces.jsx'

const Title = styled.div`
  color: ${({ theme }) => theme.colors.primaryText};
  font-weight: 600;
  margin: 0;
  ${({ theme }) => theme.fontSize('20px')};
`

const NextButtonWrapper = styled.div`
  margin-top: 22px;
`

const BigButton = styled(Button)`
  ${({ theme }) => theme.borderRadius(8)};
  background-color: ${({ theme }) => theme.colors.primaryButton};
  :hover {
    background-color: ${({ theme }) => theme.colors.primaryButtonHover};
  }
  color: ${({ theme }) => theme.colors.primaryButtonText};
  width: 100%;
  text-align: center;
  padding: 16px 14px;
  font-weight: 500;
  ${({ theme }) => theme.fontSize('16px')};
  line-height: 150%;
`

const RadioGroup = styled.div`
  margin: 16px 0;
`

const RadioLine = styled.div`
  margin: 10px 0;
`

const Space = styled.span`
  margin: 0 12px;
`

const ModalText = styled.p`
  font-size: 14px !important;
`

const FeedbackTextArea = styled.textarea`
  padding: 12px;
  line-height: 1.5 !important;
  width: 100%;
  margin: 24px 0 !important;
  border-radius: 8px;
`

const PromptFeedbackType = ({ nextClicked, T }) => {
  const [selected, setSelected] = useState('option1')

  return (
    <>
      <Title>{T('userFeedback:Select feedback topic')}</Title>

      <RadioGroup>
        <RadioLine>
          <Radio
            id="feedbackTypeMap"
            name="feedbackType"
            value="feedbackTypeMap"
            checked={selected === 'feedbackTypeMap'}
            onChange={(e) => setSelected(e.target.value)}
            label={T('userFeedback:The map or directions')}
          />
        </RadioLine>

        <RadioLine>
          <Radio
            id="feedbackTypeJourney"
            name="feedbackType"
            value="feedbackTypeJourney"
            checked={selected === 'feedbackTypeJourney'}
            onChange={(e) => setSelected(e.target.value)}
            label={T('userFeedback:My journey or connection')}
          />
        </RadioLine>
      </RadioGroup>

      <NextButtonWrapper>
        <BigButton
          onClick={() => nextClicked(selected) }
        >{T('userFeedback:Next')}</BigButton>
      </NextButtonWrapper>
    </>
  )
}

const Thanks = ({ onModalDismiss, T }) => (
  <div>
    <Title>{T('userFeedback:Thank you!')}</Title>
    <ModalText>
      {T('userFeedback:Your feedback is valuable and helps us improve and create a better application experience.')}
    </ModalText>
    <NextButtonWrapper>
      <BigButton
        onClick={() => {
          onModalDismiss()
        }}
      >{T('userFeedback:Close')}</BigButton>
    </NextButtonWrapper>
  </div>
)

const FeedbackForm = ({ submitForm, onModalDismiss, T }) => {
  const [feedbackFeeling, setFeedbackFeeling] = useState(null)
  const [comments, setComments] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()
    submitForm({
      feeling: feedbackFeeling,
      comments
    })
    onModalDismiss()
  }

  return (
    <>
      <Title>{T('userFeedback:Your feedback matters!')}</Title>
      <ModalText>
        {T('userFeedback:How is your experience with our product?')}
      </ModalText>

      <form onSubmit={handleSubmit}>
        <Radio
          id="feedbackFeelingSmile"
          name="feedbackFeeling"
          value="feedbackFeelingSmile"
          aria-label="smile"
          buttonStyle
          checked={feedbackFeeling === 'feedbackFeelingSmile'}
          color="#565656"
          label={smile}
          onChange={(e) => setFeedbackFeeling(e.target.value)}
          selectedColor="#124CB1"
        />
        <Space/>
        <Radio
          id="feedbackFeelingFrown"
          name="feedbackFeeling"
          value="feedbackFeelingFrown"
          aria-label="frown"
          buttonStyle
          checked={feedbackFeeling === 'feedbackFeelingFrown'}
          color="#565656"
          label={frown}
          onChange={(e) => setFeedbackFeeling(e.target.value)}
          selectedColor="#124CB1"
        />
        <FeedbackTextArea
          placeholder={T('userFeedback:Share any additional comments or suggestions (optional)')}
          rows="3"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
        />
        <BigButton type="submit">{T('userFeedback:Submit')}</BigButton>
      </form>
    </>
  )
}

const UserFeedbackModal = withModalWindow(({
  T,
  onModalDismiss,
  setModalDismissAction,
  submitForm,
  goCustomerURL, // can be null
  width,
  choseType
}) => {
  useEffect(() => setModalDismissAction(onModalDismiss), [])
  const [promptFeedbackType, setPromptFeedbackType] = useState(!!goCustomerURL) // if true, prompt user for feedback type
  const [displayThanks, setDisplayThanks] = useState(false) // if true, display thank you

  // Flow is FeedbackType (optional) -> FeedbackForm -> Thanks

  return displayThanks
    ? <Thanks T={T} onModalDismiss={onModalDismiss}/>
    : (promptFeedbackType
      ? <PromptFeedbackType T={T} nextClicked={selected => {
        choseType(selected)
        if (selected !== 'feedbackTypeJourney')
          setPromptFeedbackType(false)
        else {
          setDisplayThanks(true)
          goCustomerURL()
        }
      }}/>
      : <FeedbackForm T={T} submitForm={submitForm} onModalDismiss={ () => setDisplayThanks(true) }/>
    )
})

export default UserFeedbackModal
